import { Box, Button, Checkbox, FormControlLabel, Grid, Typography, SvgIcon } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetReplacementCost } from "./hook/useGetReplacementCost";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import esimIconImg from "@static/img/icons/esim.svg";
import { useGetUserContact } from "@features/Profile/UserDetails/hooks/useGetUserContact";
import { useReplacementESim } from "@features/ManageSim/hook/useReplacementESim";
import { ReplacementSimErrorModal } from "./ReplacementSimErrorModal";
import { useSimCardsRequests } from "./hook/useSimCardsRequests";

interface IParams {
    closeForm(): void;
    onSuccessCallback(): void;
}

export const ReplacementESim = (props: IParams) => {
    const { closeForm, onSuccessCallback } = props;
    const { t } = useTranslation(["sim", "common"]);
    const { getReplacementCostState, replacementCostDetails, getReplacementCostDetails } = useGetReplacementCost();
    const { errorCode } = useSimCardsRequests();

    const [acceptConditions, setAcceptConditions] = useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

    const { userDetails, getUserDetailsState, getUserDetails } = useGetUserContact();

    const error = getReplacementCostState.error ?? getUserDetailsState.error;
    const isLoading = getReplacementCostState.isLoading ?? getUserDetailsState.isLoading;
    const retryFetch = getReplacementCostState.error ? getReplacementCostDetails : getUserDetails;

    const openErrorModal = () => setIsErrorModalOpen(true);
    const closeErrorModal = () => setIsErrorModalOpen(false);

    const handleSubmit = () => {
        if (errorCode) {
            openErrorModal();
        } else {
            postReplacementESimInitial();
        }
    };

    const { postReplacementSimState, postReplacementESimInitial, postReplacementESimAfterError } = useReplacementESim({
        onSuccessCallback,
        openErrorModal,
        closeErrorModal,
    });

    return (
        <HandleErrorAndLoading error={error} isLoading={isLoading} retryFetch={retryFetch}>
            {replacementCostDetails && (
                <>
                    <Box className="replacementSim__text" display="flex" justifyContent="center">
                        <SvgIcon className="replacementSim__icon" component={esimIconImg} />
                    </Box>
                    <Grid className="replacementSim__text" container justifyContent="center">
                        <Grid item>
                            <Typography align="center" color="primary" variant="h4">
                                {t("sim:cost")}: {t("common:price", { price: replacementCostDetails.amount })}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography align="center" color="secondary" variant="normal">
                                {t("sim:replacementSimCostText")}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Typography
                        align="center"
                        className="replacementSim-emailAddress"
                        color="secondary"
                        gutterBottom
                        variant="h3"
                    >
                        {t("sim:emailAddress")}
                    </Typography>
                    <Grid container direction="column" justifyContent="center">
                        <Grid item>
                            <Typography align="center" className="replacementSim__typography">
                                {t("sim:emailConfirmationText")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <div className="replacementSim-emailAddressContent">
                                <Typography align="center">{userDetails?.email}</Typography>
                            </div>
                        </Grid>
                        <Grid item>
                            <Typography align="center" color="secondary" variant="h5">
                                {t("sim:lossOfMobileServiceTitle")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography align="center" className="replacementSim__typography" paragraph>
                                {t("sim:lossOfMobileServiceText")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                className="replacementSim-agreeingConditions"
                                control={
                                    <Checkbox
                                        className="replacementSim__checkbox"
                                        color="secondary"
                                        onChange={() => setAcceptConditions(!acceptConditions)}
                                    />
                                }
                                disabled={false}
                                label={t("sim:understandAndAgreeEsimConditions")}
                            />
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="center" spacing={2}>
                        <Grid item>
                            <Button color="secondary" size="large" variant="outlined" onClick={closeForm}>
                                {t("common:cancel")}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                color="secondary"
                                disabled={postReplacementSimState.isLoading || !acceptConditions}
                                id="submit"
                                size="large"
                                type="submit"
                                variant="contained"
                                onClick={handleSubmit}
                            >
                                {t("sim:sendNewSim")}
                            </Button>
                        </Grid>
                    </Grid>
                    {isErrorModalOpen && (
                        <ReplacementSimErrorModal
                            close={closeErrorModal}
                            confirm={postReplacementESimAfterError}
                            errorCode={errorCode}
                            postReplacementSimState={postReplacementSimState}
                        />
                    )}
                </>
            )}
        </HandleErrorAndLoading>
    );
};
